import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { PeriodKey } from '@/lib/periods';

export interface CustomRange {
  /** Local IST date strings YYYY-MM-DD (inclusive on both ends). */
  start: string;
  end: string;
}

interface DashboardCtx {
  period: PeriodKey;
  setPeriod: (p: PeriodKey, customRange?: CustomRange) => void;
  /** Present only when period === 'custom'. */
  customRange: CustomRange | null;
  outletId: string | 'all';
  setOutletId: (id: string | 'all') => void;
}

const Ctx = createContext<DashboardCtx | null>(null);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<PeriodKey>('today');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [outletId, setOutletId] = useState<string | 'all'>('all');

  // Single setter — if you pass period='custom', supply a range too.
  // Picking any other preset clears the stored custom range.
  const setPeriod = useCallback((p: PeriodKey, range?: CustomRange) => {
    setPeriodState(p);
    if (p === 'custom' && range) setCustomRange(range);
    else if (p !== 'custom') setCustomRange(null);
  }, []);

  const value = useMemo<DashboardCtx>(
    () => ({ period, setPeriod, customRange, outletId, setOutletId }),
    [period, customRange, setPeriod, outletId],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDashboard(): DashboardCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}
